import type { StackHistory } from './use-undo-stack';

/**
 * UNDO HISTORIES FOR TASKS THE SESSION HAS ALREADY VISITED.
 *
 * Loading a task used to throw its history away, which was right when every task change reloaded
 * the whole editor. The stepper arrows changed that: stepping to the next question and back is
 * now a two-click move, and losing everything you could undo because you glanced at the next
 * question is the kind of thing that teaches people not to glance.
 *
 * ## The revision is half the key
 *
 * A step holds the data a field had BEFORE an edit. Replaying that onto content someone else has
 * since changed does not undo anything — it overwrites their work with a value from a version
 * that no longer exists. So a history comes back only when the task comes back at exactly the
 * `contentRevision` it was parked at. Failing closed costs an undo history; the alternative costs
 * somebody else's edit.
 *
 * ## For the session, not beyond it
 *
 * The caller holds this in a ref, so a reload starts clean. Undo has never survived a reload and
 * should not start to here: the steps describe a document the next load re-fetches anyway.
 */
export interface ParkedUndo {
  history: StackHistory;
  revision: number;
}

export type ParkedUndoMap = Map<string, ParkedUndo>;

/**
 * How many TASKS' histories to hold. Not a depth — the steps within one task are not capped, and
 * never have been.
 *
 * Sized to cover a whole experience rather than a few steps back, because only tasks that were
 * actually EDITED are in here (an empty history is not parked), so the limit counts work done
 * rather than tasks walked past. At ten, authoring straight through a twenty-task experience and
 * then going back to fix task 3 would find its history gone — which is exactly the moment someone
 * wants it.
 *
 * Fifty is affordable: measured across the blocks in this database, a block's `data` runs about
 * 770 bytes at the median, 4 KB at p90 and 23 KB at its very largest. A step holds the before and
 * after of one block, so even a busy fifty-step history is a few hundred kilobytes, and fifty of
 * those is single-digit megabytes in a tab that already holds the whole experience.
 */
export const PARKED_UNDO_LIMIT = 50;

/**
 * Park `history` under `taskId`, evicting the least recently parked once the limit is passed.
 *
 * An empty history is not parked — there is nothing to come back to, and storing it would push a
 * real one out of the map. Re-parking a task moves it to the back of the eviction order, so the
 * tasks being worked on are the ones that survive.
 */
export function parkUndo(
  parked: ParkedUndoMap,
  taskId: string,
  history: StackHistory,
  revision: number,
  limit = PARKED_UNDO_LIMIT,
): void {
  if (history.ops.length === 0) return;
  parked.delete(taskId);
  // Copied in: the live stack keeps pushing onto its own array, and a parked history that shared
  // it would quietly grow steps belonging to another task.
  parked.set(taskId, { history: { ops: [...history.ops], cursor: history.cursor }, revision });
  while (parked.size > limit) {
    const oldest = parked.keys().next().value;
    if (oldest === undefined) break;
    parked.delete(oldest);
  }
}

/**
 * The history for `taskId` if it is still valid at `revision`, and `null` otherwise.
 *
 * Removed from the map either way: one that is handed back is live again, and one that is stale
 * is never going to become valid.
 */
export function takeUndo(parked: ParkedUndoMap, taskId: string | undefined, revision: number): StackHistory | null {
  if (!taskId) return null;
  const entry = parked.get(taskId);
  parked.delete(taskId);
  return entry && entry.revision === revision ? entry.history : null;
}
